import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArchiveRestore,
  FileText,
  History,
  KeyRound,
  Pencil,
  UserCheck,
  UserX,
  Users,
} from 'lucide-react';
import { apiRequest, ApiError, type ListResponse } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { timeAgo } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { RelationPanel } from '../components/shared/RelationPanel';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import { StatusBadge } from '../components/people/StatusBadge';
import { OverflowMenu } from '../components/people/OverflowMenu';
import { PortalAccessCard } from '../components/system/PortalAccessCard';
import {
  TeacherDrawer,
  type TeacherDrawerMode,
} from '../components/people/TeacherDrawer';
import { decodeStringArray, type TeacherRecord } from '../components/people/types';
import { type BatchRecord } from '../components/academics/types';

export const Route = createFileRoute('/_authed/people/teachers/$teacherId')({
  component: TeacherDetailPage,
});

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

interface TeacherBatchRow extends BatchRecord {
  enrolledCount: number;
}

type Tab = 'profile' | 'batches' | 'portal' | 'activity';

const TABS: ReadonlyArray<{ value: Tab; label: string; icon: typeof Users }> = [
  { value: 'profile', label: 'Profile', icon: FileText },
  { value: 'batches', label: 'Batches', icon: Users },
  { value: 'portal', label: 'Portal access', icon: KeyRound },
  { value: 'activity', label: 'Activity', icon: History },
];

// We bump the page size on the teacher's batches query so the KPI strip can
// reflect totals accurately for the MVP (a single teacher will rarely have
// more than a handful of batches). Real pagination lives on the batches list.
const BATCHES_PAGE_SIZE = 100;

function TeacherDetailPage(): JSX.Element {
  const { teacherId } = Route.useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const callerId = useAuthStore((s) => s.user?.id ?? null);
  const canManage = role === 'super_admin' || role === 'academic_admin';
  const isSuperAdmin = role === 'super_admin';

  const [tab, setTab] = useState<Tab>('profile');
  const [editDrawer, setEditDrawer] = useState<TeacherDrawerMode | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const teacherQuery = useQuery({
    queryKey: ['teacher', 'detail', teacherId] as const,
    queryFn: ({ signal }) =>
      apiRequest<TeacherRecord>(
        `/api/teachers/${encodeURIComponent(teacherId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const batchesQuery = useQuery({
    queryKey: ['teacher', 'batches', teacherId] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponse<TeacherBatchRow>>('/api/batches', {
        query: { teacherId, pageSize: BATCHES_PAGE_SIZE },
        signal,
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const teacher = teacherQuery.data;
  const batches = batchesQuery.data?.items ?? [];
  const activeBatches = useMemo(
    () => batches.filter((b) => b.status === 'active'),
    [batches],
  );
  const studentsTaught = useMemo(
    () => activeBatches.reduce((acc, b) => acc + (b.enrolledCount ?? 0), 0),
    [activeBatches],
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['teacher', 'detail', teacherId] });
    void queryClient.invalidateQueries({ queryKey: ['teacher', 'batches', teacherId] });
    void queryClient.invalidateQueries({ queryKey: ['teachers'] });
  };

  const archiveMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(`/api/teachers/${encodeURIComponent(teacherId)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      toast('Teacher archived.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not archive teacher.', {
        variant: 'error',
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/teachers/${encodeURIComponent(teacherId)}/restore`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast('Teacher restored.', { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not restore teacher.', {
        variant: 'error',
      });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/teachers/${encodeURIComponent(teacherId)}/deactivate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast(`${teacher?.name ?? 'Teacher'} deactivated.`, { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not deactivate teacher.', {
        variant: 'error',
      });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(
        `/api/teachers/${encodeURIComponent(teacherId)}/reactivate`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      toast(`${teacher?.name ?? 'Teacher'} reactivated.`, { variant: 'success' });
      invalidate();
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not reactivate teacher.', {
        variant: 'error',
      });
    },
  });

  const handleArchive = async (_id: string, name: string): Promise<void> => {
    const ok = await confirm({
      title: 'Archive teacher?',
      description: `${name} will be moved to archived status and their portal login will be disabled.`,
      confirmLabel: 'Archive',
      variant: 'danger',
    });
    if (!ok) return;
    setOverflowOpen(false);
    archiveMutation.mutate();
  };

  const handleRestore = async (): Promise<void> => {
    if (!teacher) return;
    const ok = await confirm({
      title: 'Restore teacher?',
      description: `${teacher.name} will be returned to active status.`,
      confirmLabel: 'Restore',
    });
    if (!ok) return;
    restoreMutation.mutate();
  };

  const handleDeactivate = async (): Promise<void> => {
    if (!teacher) return;
    const ok = await confirm({
      title: `Deactivate ${teacher.name}?`,
      description:
        'Their portal login will be disabled immediately and all active sessions ended. They stay on the active roster — use Archive to file them away instead.',
      confirmLabel: 'Deactivate',
      variant: 'danger',
    });
    if (!ok) return;
    deactivateMutation.mutate();
  };

  const handleReactivate = async (): Promise<void> => {
    if (!teacher) return;
    const ok = await confirm({
      title: `Reactivate ${teacher.name}?`,
      description: `${teacher.name} will be returned to active status.`,
      confirmLabel: 'Reactivate',
    });
    if (!ok) return;
    reactivateMutation.mutate();
  };

  const kpiItems = useMemo<KpiItem[]>(() => {
    if (!teacher) return [];
    return [
      {
        label: 'Active batches',
        value: activeBatches.length,
        onClick: () => setTab('batches'),
      },
      {
        label: 'Students taught',
        value: studentsTaught,
        onClick: () => setTab('batches'),
      },
      {
        label: 'Joined',
        value: timeAgo(teacher.createdAt),
      },
      {
        label: 'Portal',
        value: !teacher.userId
          ? 'Not invited'
          : teacher.status === 'inactive'
            ? 'Deactivated'
            : teacher.status === 'archived'
              ? 'Disabled'
              : 'Active',
        onClick: () => setTab('portal'),
      },
    ];
  }, [teacher, activeBatches.length, studentsTaught]);

  const archived = !!teacher?.deletedAt || teacher?.status === 'archived';
  const isSelfLinked = !!teacher?.userId && teacher.userId === callerId;
  const canDeactivate =
    isSuperAdmin &&
    !archived &&
    teacher?.status === 'active' &&
    !!teacher?.userId &&
    !isSelfLinked;
  const canReactivate =
    isSuperAdmin && !archived && teacher?.status === 'inactive';

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={teacherQuery.error}
        title="Teacher"
        onRetry={() => teacherQuery.refetch()}
      >
        {teacherQuery.isLoading || !teacher ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={teacher.name}
              sublabel={
                <>
                  <code className="rounded bg-[color:var(--color-surface)] px-1.5 py-0.5 font-mono text-[11px] text-[color:var(--color-text-muted)]">
                    {teacher.employeeId}
                  </code>
                  <span aria-hidden="true">·</span>
                  <span>{teacher.email}</span>
                </>
              }
              statusChip={<StatusBadge status={teacher.status} />}
              backTo="/people/teachers"
              actions={
                canManage ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditDrawer({ kind: 'edit', id: teacher.id })}
                    >
                      Edit
                    </Button>
                    {archived ? (
                      <Button
                        size="sm"
                        icon={<ArchiveRestore size={14} aria-hidden="true" />}
                        onClick={() => {
                          void handleRestore();
                        }}
                        loading={restoreMutation.isPending}
                      >
                        Restore
                      </Button>
                    ) : null}
                    {canReactivate ? (
                      <Button
                        size="sm"
                        icon={<UserCheck size={14} aria-hidden="true" />}
                        onClick={() => {
                          void handleReactivate();
                        }}
                        loading={reactivateMutation.isPending}
                      >
                        Reactivate
                      </Button>
                    ) : null}
                    {canDeactivate ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<UserX size={14} aria-hidden="true" />}
                        className="text-danger hover:bg-[color:var(--color-danger)]/10"
                        onClick={() => {
                          void handleDeactivate();
                        }}
                        loading={deactivateMutation.isPending}
                      >
                        Deactivate
                      </Button>
                    ) : null}
                    {!archived ? (
                      <OverflowMenu
                        open={overflowOpen}
                        onOpenChange={setOverflowOpen}
                        pending={archiveMutation.isPending}
                        onArchive={() => {
                          void handleArchive(teacher.id, teacher.name);
                        }}
                      />
                    ) : null}
                  </>
                ) : null
              }
            />

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Teacher sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TABS.map(({ value, label, icon: Icon }) => {
                const active = tab === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    <Icon size={14} aria-hidden="true" />
                    {label}
                  </button>
                );
              })}
            </div>

            {tab === 'profile' ? <ProfileTab teacher={teacher} /> : null}
            {tab === 'batches' ? (
              <BatchesTab
                batches={batches}
                isLoading={batchesQuery.isLoading && !batchesQuery.data}
                error={
                  batchesQuery.isError
                    ? batchesQuery.error instanceof ApiError
                      ? batchesQuery.error.message
                      : 'Could not load batches.'
                    : null
                }
                onRetry={() => {
                  void batchesQuery.refetch();
                }}
              />
            ) : null}
            {tab === 'portal' ? <PortalTab teacher={teacher} onChanged={invalidate} /> : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="teacher"
                targetId={teacher.id}
                title="Activity"
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      {canManage && teacher ? (
        <TeacherDrawer
          mode={editDrawer}
          onClose={() => setEditDrawer(null)}
          initial={teacher}
          isLoadingInitial={false}
          loadError={null}
          showArchive={false}
          onPortalChange={invalidate}
          onSaved={() => {
            setEditDrawer(null);
            invalidate();
            toast('Teacher saved.', { variant: 'success' });
          }}
          onArchive={async (id, name) => {
            await handleArchive(id, name);
            setEditDrawer(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Profile tab
// ---------------------------------------------------------------------------

function ProfileTab({ teacher }: { teacher: TeacherRecord }): JSX.Element {
  const qualifications = decodeStringArray(teacher.qualifications);
  const languages = decodeStringArray(teacher.languages);
  const programs = decodeStringArray(teacher.canTeachPrograms);

  return (
    <section className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Identity
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailRow
            label="Employee ID"
            value={<code className="font-mono text-xs">{teacher.employeeId}</code>}
          />
          <DetailRow label="Date of birth" value={teacher.dateOfBirth || '—'} />
          <DetailRow
            label="Gender"
            value={<span className="capitalize">{teacher.gender}</span>}
          />
          <DetailRow label="Status" value={<StatusBadge status={teacher.status} />} />
        </dl>
      </div>
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Contact
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailRow label="Email" value={teacher.email} />
          <DetailRow label="Phone" value={teacher.contact} />
          <DetailRow
            label="Address"
            value={
              <span className="whitespace-pre-wrap text-[color:var(--color-text)]">
                {teacher.address}
              </span>
            }
          />
        </dl>
      </div>
      <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 lg:col-span-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Teaching profile
        </h3>
        <dl className="mt-3 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <DetailRow
            label="Qualifications"
            value={
              qualifications.length === 0 ? (
                '—'
              ) : (
                <ChipList items={qualifications} />
              )
            }
          />
          <DetailRow
            label="Languages"
            value={
              languages.length === 0 ? '—' : <ChipList items={languages} />
            }
          />
          <DetailRow
            label="Can teach"
            value={programs.length === 0 ? '—' : <ChipList items={programs} />}
          />
          {teacher.bio ? (
            <div className="sm:col-span-2 flex flex-col gap-1 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
              <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
                Bio
              </dt>
              <dd className="text-sm text-[color:var(--color-text)] whitespace-pre-wrap">
                {teacher.bio}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>
    </section>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[color:var(--color-border)] pb-2 last:border-b-0 last:pb-0">
      <dt className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </dt>
      <dd className="text-sm text-[color:var(--color-text)] text-right">{value}</dd>
    </div>
  );
}

function ChipList({ items }: { items: ReadonlyArray<string> }): JSX.Element {
  return (
    <div className="flex flex-wrap justify-end gap-1.5">
      {items.map((item) => (
        <span
          key={item}
          className="inline-flex items-center rounded-full bg-[color:var(--color-surface)] px-2 py-0.5 text-[11px] text-[color:var(--color-text)]"
        >
          {item}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Batches tab
// ---------------------------------------------------------------------------

interface BatchesTabProps {
  batches: TeacherBatchRow[];
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
}

function BatchesTab({ batches, isLoading, error, onRetry }: BatchesTabProps): JSX.Element {
  return (
    <RelationPanel
      title="Batches"
      count={batches.length}
      description="Batches where this teacher is assigned."
      loading={isLoading}
      error={error ? { message: error, onRetry } : null}
      isEmpty={!isLoading && batches.length === 0}
      empty={{ copy: 'Not assigned to any batches yet.' }}
    >
      <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg)]">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)] text-left text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
            <tr>
              <th className="px-4 py-2">Batch</th>
              <th className="px-4 py-2">Mode</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Enrolled</th>
              <th className="px-4 py-2 text-right">Open</th>
            </tr>
          </thead>
          <tbody>
            {batches.map((b) => (
              <tr
                key={b.id}
                className="border-t border-[color:var(--color-border)] hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-4 py-2 align-middle font-medium text-[color:var(--color-text)]">
                  {b.name}
                </td>
                <td className="px-4 py-2 align-middle text-xs capitalize text-[color:var(--color-text-muted)]">
                  {b.mode}
                </td>
                <td className="px-4 py-2 align-middle">
                  <BatchStatusChip status={b.status} />
                </td>
                <td className="px-4 py-2 align-middle text-right text-xs tabular-nums text-[color:var(--color-text-muted)]">
                  {b.enrolledCount}
                  {b.capacity > 0 ? (
                    <span className="text-[color:var(--color-text-muted)]">
                      {' '}
                      / {b.capacity}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-2 align-middle text-right">
                  <Link
                    to="/academics/batches/$batchId"
                    params={{ batchId: b.id }}
                    className="text-xs font-medium text-gold-700 hover:underline"
                  >
                    Open batch
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </RelationPanel>
  );
}

function BatchStatusChip({ status }: { status: string }): JSX.Element {
  const tone =
    status === 'active'
      ? 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]'
      : status === 'draft'
        ? 'bg-gold-50 text-gold-700 dark:bg-gold-700/20'
        : status === 'completed'
          ? 'bg-[color:var(--color-surface)] text-[color:var(--color-text)]'
          : 'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]';
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize',
        tone,
      )}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Portal access tab
// ---------------------------------------------------------------------------

function PortalTab({
  teacher,
  onChanged,
}: {
  teacher: TeacherRecord;
  onChanged: () => void;
}): JSX.Element {
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <PortalAccessCard
        profileType="teacher"
        profileId={teacher.id}
        profileName={teacher.name}
        profileEmail={teacher.email}
        userId={teacher.userId}
        onChange={onChanged}
      />
    </section>
  );
}
